// frontend/src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Link,
  TextField,
  Divider,
  keyframes
} from
  '@mui/material';
import { useAuth } from '../contexts/AuthContext'; import { useThemeSettings } from "../contexts/ThemeContext";
import { APP_VERSION_LABEL, formatBuildDate } from '../utils/appVersion';
import { glassSurface } from '@caro/shared/theme';

// Keyframe animations
const pulseGlow = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 10px rgba(154, 63, 105, 0.3));
  }
  50% {
    transform: scale(1.05);
    filter: drop-shadow(0 0 20px rgba(154, 63, 105, 0.6));
  }
`;

const floatAnimation = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 0.6;
  }
  100% {
    transform: translateY(0px) rotate(360deg);
    opacity: 0.3;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
`;

const Login = () => {
  const { t } = useThemeSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithPassword, error: authError, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Password sign-in (spec 20260819-registrationandonboarding, D4). Customers provisioned
  // through onboarding have NO Entra tenant by construction, so Microsoft is not an option
  // for them -- this form is their only way in.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Get redirect path from URL if available
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo') || '/dashboard';
  const urlError = params.get('error');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handlePasswordLogin = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim() || !password) return;

    setError('');
    setPasswordLoading(true);
    try {
      const result = await loginWithPassword(email.trim(), password);
      if (result?.success) {
        navigate(redirectTo || '/dashboard', { replace: true });
      } else {
        // The server is deliberately vague here: a wrong e-mail, a wrong password and an
        // SSO-only account all answer the same. Do not "improve" this message.
        setError(result?.error || (t ? t('Login.invalid_credentials') : 'Invalid email or password'));
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setError('');

    try {
      // Call login without a token to initiate redirect to Microsoft
      await login(null, redirectTo);
      // The redirect happens in the login function
    } catch (err) {
      setError('Failed to initiate login');
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'AccessDenied':
        return 'You don\'t have access to this application. Please contact your administrator.';
      case 'no_code':
        return 'Authentication failed. Please try again.';
      case 'token_error':
        return 'There was a problem with authentication. Please try again.';
      case 'auth_error':
        return 'An error occurred during login. Please try again.';
      case 'session_expired':
        return 'Your session has expired. Please log in again.';
      default:
        return 'An error occurred during login. Please try again.';
    }
  };

  return (
    <Box
      className="page-transition-enter"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        backgroundImage: 'linear-gradient(135deg, rgba(154, 63, 105, 0.08) 0%, rgba(218, 160, 61, 0.08) 25%, rgba(154, 63, 105, 0.12) 50%, rgba(218, 160, 61, 0.08) 75%, rgba(154, 63, 105, 0.08) 100%)',
        backgroundSize: '400% 400%',
        animation: `${gradientShift} 15s ease infinite`,
        position: 'relative',
        overflow: 'hidden'
      }}>

      {/* Large decorative blobs */}
      <Box sx={{
        position: 'absolute',
        top: '-10%',
        left: '-5%',
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(154, 63, 105, 0.15), transparent 70%)',
        filter: 'blur(40px)',
        animation: `${pulse} 8s ease-in-out infinite`,
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        bottom: '-10%',
        right: '-5%',
        width: 350,
        height: 350,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(218, 160, 61, 0.12), transparent 70%)',
        filter: 'blur(50px)',
        animation: `${pulse} 10s ease-in-out infinite`,
        animationDelay: '2s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '-10%',
        width: 250,
        height: 250,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(154, 63, 105, 0.08), transparent 70%)',
        filter: 'blur(60px)',
        animation: `${pulse} 12s ease-in-out infinite`,
        animationDelay: '4s',
        pointerEvents: 'none'
      }} />

      {/* Floating background particles */}
      <Box sx={{
        position: 'absolute',
        top: '10%',
        left: '15%',
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(154, 63, 105, 0.2), transparent)',
        animation: `${floatAnimation} 8s ease-in-out infinite`,
        animationDelay: '0s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(218, 160, 61, 0.2), transparent)',
        animation: `${floatAnimation} 10s ease-in-out infinite`,
        animationDelay: '2s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '20%',
        right: '10%',
        width: 50,
        height: 50,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(154, 63, 105, 0.18), transparent)',
        animation: `${floatAnimation} 9s ease-in-out infinite`,
        animationDelay: '4s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        bottom: '15%',
        right: '15%',
        width: 45,
        height: 45,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(218, 160, 61, 0.18), transparent)',
        animation: `${floatAnimation} 11s ease-in-out infinite`,
        animationDelay: '6s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '40%',
        left: '8%',
        width: 35,
        height: 35,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(154, 63, 105, 0.15), transparent)',
        animation: `${floatAnimation} 9.5s ease-in-out infinite`,
        animationDelay: '1s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '75%',
        right: '8%',
        width: 55,
        height: 55,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(218, 160, 61, 0.16), transparent)',
        animation: `${floatAnimation} 10.5s ease-in-out infinite`,
        animationDelay: '3s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '30%',
        right: '25%',
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(154, 63, 105, 0.12), transparent)',
        animation: `${floatAnimation} 7.5s ease-in-out infinite`,
        animationDelay: '5s',
        pointerEvents: 'none'
      }} />

      <Box sx={{
        position: 'absolute',
        bottom: '40%',
        left: '20%',
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(218, 160, 61, 0.14), transparent)',
        animation: `${floatAnimation} 8.5s ease-in-out infinite`,
        animationDelay: '7s',
        pointerEvents: 'none'
      }} />

      <Paper
        elevation={3}
        className="card-animated card-enter"
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          borderRadius: 2,
          animation: `${scaleIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
          // Lag op 0.95 wit: technisch glas, visueel een vlak. Nu hetzelfde oppervlak
          // als de rest, met de pruimgloed die deze pagina eigen is eroverheen.
          ...glassSurface(),
          boxShadow: '0 8px 32px rgba(154, 63, 105, 0.18), 0 0 0 1px rgba(154, 63, 105, 0.06)',
          position: 'relative',
          zIndex: 1
        }}>

        <Box
          component="img"
          className="fade-in"
          sx={{
            width: 250,
            mb: 3,
            animation: `${pulseGlow} 3s ease-in-out infinite`
          }}
          alt="CARO"
          src='/logo/logo-full.png' />


        <Typography
          variant="h5"
          className="slide-down"
          gutterBottom
          sx={{
            color: 'primary.main',
            fontWeight: 'medium',
            animation: `${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards`
          }}>
          {t ? t("Login.welcome_caro_portal") : "Welcome to CARO Portal"}
        </Typography>

        <Typography
          variant="body1"
          className="fade-in"
          color="text.secondary"
          sx={{
            mb: 4,
            animation: `${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s forwards`
          }}>
          {t ? t("Login.subtitle_both") : "Sign in to the CARO Portal"}
        </Typography>

        {(error || authError || urlError) &&
          <Alert severity="error" className="slide-up" sx={{ mb: 3 }}>
            {/* De eigen fout eerst. getErrorMessage geeft ook zonder foutcode een standaardtekst
                terug, dus vooraan verborg die elke echte melding achter "An error occurred
                during login" (testplan 2026-10-15). */}
            {error || authError || getErrorMessage(urlError)}
          </Alert>
        }

        {/* E-mail + password. First, because for a customer onboarded through the
            registration flow it is the ONLY method that works -- they have no Entra
            tenant. Microsoft stays below for the existing tenants. */}
        <Box component="form" onSubmit={handlePasswordLogin} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            type="email"
            autoComplete="username"
            label={t ? t('Login.email') : 'E-mail'}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={passwordLoading || loading}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            autoComplete="current-password"
            label={t ? t('Login.password') : 'Password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={passwordLoading || loading}
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={passwordLoading || loading || !email.trim() || !password}
            sx={{ py: 1.5 }}
          >
            {passwordLoading ? <CircularProgress size={22} /> : (t ? t('Login.sign_in') : 'Sign in')}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'right', mt: -1, mb: 1 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => navigate('/reset-password')}
          >
            {t ? t('Login.forgot_password') : 'Forgot your password?'}
          </Link>
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t ? t('Login.or') : 'or'}
          </Typography>
        </Divider>

        <Button
          variant="outlined"
          fullWidth
          size="large"
          onClick={handleMicrosoftLogin}
          disabled={loading || passwordLoading}
          sx={{
            mb: 2,
            py: 1.5,
            position: 'relative',
            overflow: 'hidden',
            animation: `${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s forwards`,
            '&:hover:not(:disabled)': {
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: `${shimmer} 1.5s infinite`
              }
            }
          }}>

          {loading ? <div className="spinner spinner-small" /> : 'Sign in with Microsoft'}
        </Button>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 3,
            animation: `${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s forwards`
          }}>
          {t ? t("Login.your_company_account_must_be_preappro") : "Your company or account must be pre-approved to access this system.\n          For support, contact"}
          {' '}
          <Link href="mailto:support@caro.care">{t ? t("Login.supportcarocare") : "support@caro.care"}</Link>
        </Typography>


          <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.75rem',
            opacity: 0.6
          }}
          title={formatBuildDate()}>{APP_VERSION_LABEL}
        </Typography>
      </Paper>
    </Box>);

};

export default Login;